import React, { useEffect, useMemo, useState } from "react";
import { req } from "../api";
import styles from "./CheckoutRateTest.module.css";
import { CLIENT_PAYMENT_OPTIONS } from "./clientPaymentConfig";
import { PaymentIcon } from "./clientPaymentIcons";
import {
  LATAM_AFRICA_FIAT_OPTIONS,
  USD_TO_USDT_ROUTE_ID,
} from "./checkoutRateTestConfig";

function pick(obj, paths) {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    let ok = true;

    for (const part of parts) {
      if (!cur || typeof cur !== "object" || !(part in cur)) {
        ok = false;
        break;
      }
      cur = cur[part];
    }

    if (ok && cur !== undefined && cur !== null) return cur;
  }
  return null;
}

function round(value, digits = 8) {
  if (!Number.isFinite(value)) return "";
  return Number(value).toFixed(digits).replace(/\.?0+$/, "");
}

function getRouteRateNumber(routeJson) {
  const route =
    pick(routeJson, ["data.data.route", "data.route", "data.data", "data.result"]) || null;

  const rateFullNumber = Number(route?.rate?.rateFullNumber);
  if (Number.isFinite(rateFullNumber) && rateFullNumber > 0) return rateFullNumber;

  const rateOut = Number(route?.rate?.out);
  const rateIn = Number(route?.rate?.in);
  if (Number.isFinite(rateOut) && Number.isFinite(rateIn) && rateIn > 0) {
    return rateOut / rateIn;
  }

  return null;
}

async function fetchRouteRate(routeId, lang = "en") {
  const out = await req(
    `/api/poteme/public/exchanger/route/get/one?routeId=${encodeURIComponent(routeId)}&lang=${encodeURIComponent(lang)}`
  );

  const route =
    pick(out.json, ["data.data.route", "data.route", "data.data", "data.result"]) || null;

  const rate = getRouteRateNumber(out.json);

  return { route, rate, raw: out.json };
}

async function fetchCurrencyRatesFromGithub(baseCurrency) {
  const base = String(baseCurrency || "").trim().toLowerCase();
  if (!base) throw new Error("Требуется базовая валюта");

  const urls = [
    `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${base}.json`,
    `https://latest.currency-api.pages.dev/v1/currencies/${base}.json`,
  ];

  let lastError = null;

  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Currency API HTTP ${res.status}`);
      }
      const json = await res.json();
      return json;
    } catch (e) {
      lastError = e;
    }
  }

  throw lastError || new Error("Не удалось загрузить валютные курсы");
}

const RU_LABELS = {
  ARS: "Аргентинское песо",
  BOB: "Боливийский боливиано",
  BRL: "Бразильский реал",
  CLP: "Чилийское песо",
  COP: "Колумбийское песо",
  CRC: "Коста-риканский колон",
  CUP: "Кубинское песо",
  DOP: "Доминиканское песо",
  GTQ: "Гватемальский кетсаль",
  GYD: "Гайанский доллар",
  HNL: "Гондурасская лемпира",
  HTG: "Гаитянский гурд",
  JMD: "Ямайский доллар",
  MXN: "Мексиканское песо",
  NIO: "Никарагуанская кордоба",
  PAB: "Панамский бальбоа",
  PEN: "Перуанский соль",
  PYG: "Парагвайский гуарани",
  SRD: "Суринамский доллар",
  UYU: "Уругвайское песо",
  VES: "Венесуэльский боливар",
  BZD: "Белизский доллар",

  DZD: "Алжирский динар",
  AOA: "Ангольская кванза",
  BWP: "Ботсванская пула",
  BIF: "Бурундийский франк",
  CVE: "Эскудо Кабо-Верде",
  CDF: "Конголезский франк",
  DJF: "Франк Джибути",
  EGP: "Египетский фунт",
  ERN: "Эритрейская накфа",
  ETB: "Эфиопский быр",
  GHS: "Ганский седи",
  GMD: "Гамбийский даласи",
  GNF: "Гвинейский франк",
  KES: "Кенийский шиллинг",
  KMF: "Коморский франк",
  LRD: "Либерийский доллар",
  LSL: "Лесотский лоти",
  LYD: "Ливийский динар",
  MAD: "Марокканский дирхам",
  MGA: "Малагасийский ариари",
  MRU: "Мавританская угия",
  MUR: "Маврикийская рупия",
  MWK: "Малавийская квача",
  MZN: "Мозамбикский метикал",
  NAD: "Доллар Намибии",
  NGN: "Нигерийская найра",
  RWF: "Руандийский франк",
  SCR: "Сейшельская рупия",
  SDG: "Суданский фунт",
  SLL: "Сьерра-леонский леоне",
  SOS: "Сомалийский шиллинг",
  SSP: "Южносуданский фунт",
  STN: "Добра Сан-Томе и Принсипи",
  SZL: "Свазилендский лилангени",
  TND: "Тунисский динар",
  TZS: "Танзанийский шиллинг",
  UGX: "Угандийский шиллинг",
  XAF: "Центральноафриканский франк КФА",
  XOF: "Западноафриканский франк КФА",
  ZAR: "Южноафриканский ранд",
  ZMW: "Замбийская квача",
  ZWL: "Валюта Зимбабве",

  USD: "Доллар США",
};

const RU_REGION = {
  "Latin America": "Латинская Америка",
  Africa: "Африка",
};

export default function CheckoutRateTest() {
  const paymentOptions = useMemo(
    () => CLIENT_PAYMENT_OPTIONS.filter((x) => x.routeId),
    []
  );

  const [amount, setAmount] = useState("100");
  const [selectedFiat, setSelectedFiat] = useState("BRL");
  const [selectedPaymentKey, setSelectedPaymentKey] = useState(
    paymentOptions.find((x) => x.key === "usdt_trc20")?.key || paymentOptions[0]?.key || ""
  );
  const [markupPercent, setMarkupPercent] = useState(0);
  const [rateDisplayMode, setRateDisplayMode] = useState("usd-first"); // usd-first | fiat-first
  const [cryptoRateDisplayMode, setCryptoRateDisplayMode] = useState("crypto-first"); // crypto-first | usdt-first

  const [usdBaseMap, setUsdBaseMap] = useState({});
  const [usdToUsdtRate, setUsdToUsdtRate] = useState(null);
  const [selectedRouteRate, setSelectedRouteRate] = useState(null);

  const [loadingFx, setLoadingFx] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [error, setError] = useState("");

  const selectedOption = useMemo(
    () => paymentOptions.find((x) => x.key === selectedPaymentKey) || paymentOptions[0] || null,
    [paymentOptions, selectedPaymentKey]
  );

  const selectedFiatMeta = useMemo(
    () => LATAM_AFRICA_FIAT_OPTIONS.find((x) => x.code === selectedFiat) || null,
    [selectedFiat]
  );

  useEffect(() => {
    async function loadUsdBaseMap() {
      setLoadingFx(true);
      try {
        const json = await fetchCurrencyRatesFromGithub("usd");
        const rates = json?.usd || {};

        if (!rates || typeof rates !== "object") {
          throw new Error("Некорректный ответ с валютными курсами");
        }

        setUsdBaseMap(rates);
      } catch (e) {
        setError((prev) => prev || e?.message || "Не удалось загрузить фиатные курсы");
      } finally {
        setLoadingFx(false);
      }
    }

    loadUsdBaseMap();
  }, []);

  useEffect(() => {
    async function loadBaseUsdToUsdtRate() {
      if (!USD_TO_USDT_ROUTE_ID || USD_TO_USDT_ROUTE_ID === "PUT_USD_TO_USDT_ROUTE_ID_HERE") {
        setError("Вставь routeId для USD → USDT в checkoutRateTestConfig.js");
        return;
      }

      setLoadingRoutes(true);
      try {
        const { rate } = await fetchRouteRate(USD_TO_USDT_ROUTE_ID);
        if (!rate || rate <= 0) {
          throw new Error("Не удалось распознать курс USD → USDT");
        }
        setUsdToUsdtRate(rate);
      } catch (e) {
        setError((prev) => prev || e?.message || "Не удалось загрузить маршрут USD → USDT");
      } finally {
        setLoadingRoutes(false);
      }
    }

    loadBaseUsdToUsdtRate();
  }, []);

  useEffect(() => {
    async function loadSelectedPaymentRoute() {
      if (!selectedOption?.routeId) {
        setSelectedRouteRate(null);
        return;
      }

      setLoadingRoutes(true);
      try {
        const { rate } = await fetchRouteRate(selectedOption.routeId);
        if (!rate || rate <= 0) {
          throw new Error("Не удалось распознать курс выбранной криптовалюты");
        }
        setSelectedRouteRate(rate);
      } catch (e) {
        setError((prev) => prev || e?.message || "Не удалось загрузить маршрут выбранной криптовалюты");
      } finally {
        setLoadingRoutes(false);
      }
    }

    loadSelectedPaymentRoute();
  }, [selectedOption?.routeId]);

  const fiatPerUsd = usdBaseMap[selectedFiat.toLowerCase()] || usdBaseMap[selectedFiat];
  const usdPerOneFiat = fiatPerUsd ? 1 / fiatPerUsd : null;

  const numericAmount = Number(amount || 0);
  const baseUsdAmount =
    Number.isFinite(numericAmount) && numericAmount > 0 && usdPerOneFiat
      ? numericAmount * usdPerOneFiat
      : null;

  const baseUsdtAmount =
    baseUsdAmount && usdToUsdtRate ? baseUsdAmount * usdToUsdtRate : null;

  const adjustedUsdtAmount =
    baseUsdtAmount != null
      ? baseUsdtAmount * (1 + Number(markupPercent || 0) / 100)
      : null;

  const selectedCryptoAmount =
    adjustedUsdtAmount && selectedRouteRate
      ? adjustedUsdtAmount * selectedRouteRate
      : null;

  const usdtPerOneFiat =
    usdPerOneFiat && usdToUsdtRate
      ? usdPerOneFiat * usdToUsdtRate * (1 + Number(markupPercent || 0) / 100)
      : null;

  const fiatPerOneUsd = fiatPerUsd || null;
  const fiatPerOneUsdt =
    usdToUsdtRate && fiatPerUsd
      ? fiatPerUsd / usdToUsdtRate / (1 + Number(markupPercent || 0) / 100)
      : null;

  const cryptoPerOneUsdt = selectedRouteRate || null;
  const usdtPerOneCrypto =
    selectedRouteRate && selectedRouteRate > 0 ? 1 / selectedRouteRate : null;

  const cryptoPerOneUsd =
    usdToUsdtRate && selectedRouteRate ? usdToUsdtRate * selectedRouteRate : null;

  const usdPerOneCrypto =
    usdtPerOneCrypto && usdToUsdtRate ? usdtPerOneCrypto / usdToUsdtRate : null;

  const quickRates = useMemo(() => {
    return LATAM_AFRICA_FIAT_OPTIONS.map((item) => {
      const fiatPerUsdLocal =
        usdBaseMap[item.code.toLowerCase()] || usdBaseMap[item.code];

      const usdPerOne = fiatPerUsdLocal ? 1 / fiatPerUsdLocal : null;
      const usdtPerOne =
        usdPerOne && usdToUsdtRate
          ? usdPerOne * usdToUsdtRate * (1 + Number(markupPercent || 0) / 100)
          : null;

      const oneUsdInFiat = fiatPerUsdLocal || null;
      const oneUsdtInFiat =
        usdToUsdtRate && fiatPerUsdLocal
          ? fiatPerUsdLocal / usdToUsdtRate / (1 + Number(markupPercent || 0) / 100)
          : null;

      return {
        ...item,
        usdPerOne,
        usdtPerOne,
        oneUsdInFiat,
        oneUsdtInFiat,
      };
    });
  }, [usdBaseMap, usdToUsdtRate, markupPercent]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Тест курсов оплаты</h1>
          <p className={styles.subtitle}>
            Проверка конвертации фиат → USD → USDT → выбранная криптовалюта на основе GitHub currency API и твоих Poteme маршрутов.
          </p>
        </div>

        {error ? <div className={styles.error}>{error}</div> : null}

        <div className={styles.layout}>
          <div className={styles.panel}>
            <div className={styles.section}>
              <label className={styles.label} htmlFor="amount">Сумма товара</label>
              <input
                id="amount"
                className={styles.input}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder="Введите сумму"
              />
            </div>

            <div className={styles.section}>
              <label className={styles.label} htmlFor="fiat">Фиатная валюта</label>
              <select
                id="fiat"
                className={styles.select}
                value={selectedFiat}
                onChange={(e) => setSelectedFiat(e.target.value)}
              >
                {LATAM_AFRICA_FIAT_OPTIONS.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code} — {RU_LABELS[item.code] || item.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.section}>
              <label className={styles.label}>Надбавка к курсу</label>
              <div className={styles.rangeWrap}>
                <div className={styles.rangeRow}>
                  <input
                    className={styles.range}
                    type="range"
                    min="0"
                    max="5"
                    step="0.1"
                    value={markupPercent}
                    onChange={(e) => setMarkupPercent(Number(e.target.value))}
                  />
                  <span className={styles.rangeValue}>{round(Number(markupPercent || 0), 1)}%</span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.rateModeHeader}>
                <label className={styles.label}>Отображение фиатного курса</label>
                <button
                  type="button"
                  className={styles.swapButton}
                  onClick={() =>
                    setRateDisplayMode((prev) =>
                      prev === "usd-first" ? "fiat-first" : "usd-first"
                    )
                  }
                >
                  {rateDisplayMode === "usd-first"
                    ? "Показать 1 фиат → USD / USDT"
                    : "Показать 1 USD / USDT → фиат"}
                </button>
              </div>
            </div>

            <div className={styles.section}>
              <label className={styles.label}>Способ оплаты</label>
              <div className={styles.currencyGrid}>
                {paymentOptions.map((item) => {
                  const active = item.key === selectedPaymentKey;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`${styles.currencyButton} ${active ? styles.currencyButtonActive : ""}`}
                      onClick={() => setSelectedPaymentKey(item.key)}
                    >
                      <span className={styles.currencyLeft}>
                        <PaymentIcon iconKey={item.iconKey} className={styles.currencyIcon} />
                        <span>
                          <span className={styles.currencyTitle}>{item.symbol}</span>
                          <span className={styles.currencySubtitle}>{item.network}</span>
                        </span>
                      </span>

                      {active ? <span className={styles.selectedBadge}>Выбрано</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.rateModeHeader}>
                <label className={styles.label}>Курс криптовалюты</label>
                <button
                  type="button"
                  className={styles.swapButton}
                  onClick={() =>
                    setCryptoRateDisplayMode((prev) =>
                      prev === "crypto-first" ? "usdt-first" : "crypto-first"
                    )
                  }
                >
                  {cryptoRateDisplayMode === "crypto-first"
                    ? "Показать 1 USDT / USD → крипта"
                    : "Показать 1 крипта → USDT / USD"}
                </button>
              </div>
            </div>

            <div className={styles.summary}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Выбранная фиатная валюта</div>
                <div className={styles.summaryValue}>
                  {selectedFiatMeta?.code || selectedFiat}
                </div>
                <div className={styles.summarySub}>
                  {RU_LABELS[selectedFiatMeta?.code] || selectedFiatMeta?.name || ""}
                </div>
              </div>

              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Вид фиатного курса</div>
                <div className={styles.summaryValue}>
                  {rateDisplayMode === "usd-first"
                    ? fiatPerOneUsd
                      ? `1 USD = ${round(fiatPerOneUsd, 6)} ${selectedFiat}`
                      : "—"
                    : usdPerOneFiat
                      ? `1 ${selectedFiat} = ${round(usdPerOneFiat, 8)} USD`
                      : "—"}
                </div>
                <div className={styles.summarySub}>
                  {rateDisplayMode === "usd-first"
                    ? fiatPerOneUsdt
                      ? `1 USDT = ${round(fiatPerOneUsdt, 6)} ${selectedFiat}`
                      : "—"
                    : usdtPerOneFiat
                      ? `1 ${selectedFiat} = ${round(usdtPerOneFiat, 8)} USDT`
                      : "—"}
                </div>
              </div>

              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Вид крипто-курса</div>
                <div className={styles.summaryValue}>
                  {cryptoRateDisplayMode === "crypto-first"
                    ? usdtPerOneCrypto && selectedOption
                      ? `1 ${selectedOption.symbol} = ${round(usdtPerOneCrypto, 8)} USDT`
                      : "—"
                    : cryptoPerOneUsdt && selectedOption
                      ? `1 USDT = ${round(cryptoPerOneUsdt, 12)} ${selectedOption.symbol}`
                      : "—"}
                </div>
                <div className={styles.summarySub}>
                  {cryptoRateDisplayMode === "crypto-first"
                    ? usdPerOneCrypto && selectedOption
                      ? `1 ${selectedOption.symbol} = ${round(usdPerOneCrypto, 8)} USD`
                      : "—"
                    : cryptoPerOneUsd && selectedOption
                      ? `1 USD = ${round(cryptoPerOneUsd, 12)} ${selectedOption.symbol}`
                      : "—"}
                </div>
              </div>

              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Сумма в USD</div>
                <div className={styles.summaryValue}>
                  {baseUsdAmount != null ? `${round(baseUsdAmount, 8)} USD` : "—"}
                </div>
              </div>

              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Сумма в USDT</div>
                <div className={styles.summaryValue}>
                  {baseUsdtAmount != null ? `${round(baseUsdtAmount, 8)} USDT` : "—"}
                </div>
                <div className={styles.summarySub}>
                  Через маршрут USD → USDT
                </div>
              </div>

              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>USDT с надбавкой</div>
                <div className={styles.summaryValue}>
                  {adjustedUsdtAmount != null ? `${round(adjustedUsdtAmount, 8)} USDT` : "—"}
                </div>
                <div className={styles.summarySub}>
                  Надбавка: {round(Number(markupPercent || 0), 1)}%
                </div>
              </div>

              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>
                  К оплате в {selectedOption?.symbol || "криптовалюте"}
                </div>
                <div className={styles.summaryValue}>
                  {selectedCryptoAmount != null && selectedOption
                    ? `${round(selectedCryptoAmount, 8)} ${selectedOption.symbol}`
                    : "—"}
                </div>
                <div className={styles.summarySub}>
                  {selectedOption?.title || ""}
                </div>
              </div>
            </div>

            <div className={styles.note}>
              {loadingFx || loadingRoutes
                ? "Загрузка курсов..."
                : "Фиатные курсы берутся из GitHub currency API. USD → USDT и конвертация в криптовалюту берутся из твоих Poteme маршрутов."}
            </div>
          </div>

          <div className={styles.panel}>
            <div className={styles.section}>
              <label className={styles.label}>Быстрый обзор фиатных курсов</label>
              <div className={styles.quickRates}>
                {quickRates.map((item) => (
                  <div key={item.code} className={styles.quickRateRow}>
                    <div className={styles.quickRateTop}>
                      <div className={styles.quickRateCode}>{item.code}</div>
                      <div className={styles.quickRateRegion}>
                        {RU_REGION[item.region] || item.region}
                      </div>
                    </div>
                    <div className={styles.quickRateName}>
                      {RU_LABELS[item.code] || item.name}
                    </div>
                    <div className={styles.quickRateValue}>
                      {rateDisplayMode === "usd-first" ? (
                        <>
                          1 USD = {item.oneUsdInFiat ? round(item.oneUsdInFiat, 6) : "—"} {item.code}
                          <br />
                          1 USDT = {item.oneUsdtInFiat ? round(item.oneUsdtInFiat, 6) : "—"} {item.code}
                        </>
                      ) : (
                        <>
                          1 {item.code} = {item.usdPerOne ? round(item.usdPerOne, 8) : "—"} USD
                          <br />
                          1 {item.code} = {item.usdtPerOne ? round(item.usdtPerOne, 8) : "—"} USDT
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}